import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../utils/auth';
import { useCapability } from '../utils/capabilityContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const AdminLogin = () => {
  const [adminToken, setAdminToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const { refresh: refreshCapabilities } = useCapability();
  const isZh = i18n.language.startsWith('zh');

  useEffect(() => {
    const existingToken = localStorage.getItem('admin_token');
    if (!existingToken) {
      return;
    }

    axios
      .get('/api/v1/doctors', {
        headers: {
          Authorization: `Bearer ${existingToken}`
        }
      })
      .then(async () => {
        await refreshCapabilities();
        navigate('/doctors');
      })
      .catch(async () => {
        localStorage.removeItem('admin_token');
        await refreshCapabilities();
      });
  }, [navigate, refreshCapabilities]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.get('/api/v1/doctors', {
        headers: {
          Authorization: `Bearer ${adminToken}`
        }
      });

      await logout();
      localStorage.setItem('admin_token', adminToken);
      await refreshCapabilities();
      navigate('/doctors');
    } catch (requestError) {
      console.error('管理员登录失败:', requestError);
      setError(t('dashboard.adminAuthFailed'));
    } finally {
      setLoading(false);
    }
  };

  const guardrailItems = isZh
    ? [
        { label: '安全验证', value: '仅限授权管理员登录，保障系统安全' },
        { label: '独立会话', value: '登录后自动切换至管理员模式' },
        { label: '医生管理', value: '集中管理医生账号、权限和基本信息' }
      ]
    : [
        { label: 'Secure access', value: 'Only authorized administrators can sign in.' },
        { label: 'Separate session', value: 'Signing in switches to admin mode automatically.' },
        { label: 'Doctor management', value: 'Manage doctor accounts, permissions, and profiles in one place.' }
      ];

  return (
    <div className="auth-screen">
      <div className="page-shell" style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LanguageSwitcher />
        </div>

        <div className="auth-shell">
          <section className="surface-panel surface-panel--accent auth-panel">
            <div className="surface-panel__body auth-brand">
              <div className="auth-brand__copy">
                <span className="page-eyebrow">{t('dashboard.admin')}</span>
                <h1 className="auth-brand__headline">
                  {isZh ? '管理员登录' : 'Administrator Login'}
                </h1>
                <p className="auth-brand__summary">
                  {isZh
                    ? '输入管理员授权码，进入医生账号管理和系统维护功能。'
                    : 'Enter your administrator token to access doctor account management and system maintenance.'}
                </p>
                <div className="auth-form__actions">
                  <button
                    type="button"
                    className="theme-button theme-button--primary"
                    onClick={() => navigate('/login')}
                  >
                    {t('dashboard.doctorLogin')}
                  </button>
                  <button
                    type="button"
                    className="theme-button theme-button--ghost"
                    onClick={() => navigate('/')}
                  >
                    {t('dashboard.title')}
                  </button>
                </div>
              </div>

              <div className="auth-brand__stats">
                {guardrailItems.map((item) => (
                  <div key={item.label} className="auth-brand__stat">
                    <span className="auth-brand__stat-label">{item.label}</span>
                    <span className="auth-brand__stat-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="surface-panel auth-panel">
            <div className="surface-panel__body auth-form">
              <div className="auth-form__header">
                <span className="page-eyebrow">{t('dashboard.adminLogin')}</span>
                <h2 className="auth-form__title">{t('dashboard.adminLogin')}</h2>
                <p className="auth-form__subtitle">
                  {t('dashboard.adminAuthRequired')}
                </p>
              </div>

              {error && (
                <div className="status-banner status-banner--error">
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="stack">
                <div className="field">
                  <label htmlFor="admin-token" className="field__label">
                    {t('dashboard.adminToken')}
                  </label>
                  <input
                    id="admin-token"
                    type="password"
                    className="theme-input"
                    value={adminToken}
                    onChange={(event) => setAdminToken(event.target.value)}
                    placeholder={t('dashboard.enterAdminToken')}
                    autoComplete="current-password"
                    required
                  />
                  <span className="field__hint">
                    {isZh
                      ? '验证通过后将进入医生管理页面。'
                      : 'You will be redirected to the doctor management page after verification.'}
                  </span>
                </div>

                <div className="auth-form__actions">
                  <button
                    type="submit"
                    className="theme-button theme-button--primary theme-button--full"
                    disabled={loading}
                  >
                    {loading ? t('dashboard.verifying') : t('dashboard.verify')}
                  </button>
                </div>
              </form>

              <div className="status-banner">
                <span>
                  {isZh
                    ? '此入口仅用于医生账号管理，不涉及患者数据操作。'
                    : 'This portal is for doctor account management only and does not access patient data.'}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
